import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";

const IMG_SPESI =
    "https://images.pexels.com/photos/4177735/pexels-photo-4177735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_ACC =
    "https://images.unsplash.com/photo-1586324304780-c9a5031a3599?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBtZWRpY2FsJTIwdmlzaXQlMjBmcmllbmRseXxlbnwwfHx8fDE3OTI0MjkwNzZ8MA&ixlib=rb-4.1.0&q=85";
const IMG_PASSEGGIATE =
    "https://images.pexels.com/photos/38034768/pexels-photo-38034768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = {
    spese: {
        n: "01",
        title: "Facciamo le spese con te",
        desc: "Supermercato, farmacia, commissioni in centro: giriamo i negozi insieme oppure facciamo noi la spesa, con la lista in mano e la cura di sempre.",
        chips: ["Supermercato", "Farmacia", "Commissioni"],
    },
    visite: {
        n: "02",
        title: "Visite mediche, andata e ritorno",
        desc: "Ti accompagniamo alla visita, dentro e fuori dalla sala d'attesa, e ti aiutiamo a capire con calma le indicazioni del medico.",
        chips: ["Su prenotazione", "Andata e ritorno", "Sala d'attesa"],
        img: "/servizio-visite.jpg",
        alt: "Due amiche di età diverse camminano a braccio unito verso un appuntamento",
    },
    accompagnamento: {
        n: "03",
        title: "Accompagnamento, ovunque serva",
        desc: "Un braccio su cui appoggiarsi: centri medici, uffici, eventi, casa di amici. Andiamo e torniamo insieme, sempre.",
        chips: ["Centri medici", "Uffici", "Eventi"],
        img: IMG_ACC,
        alt: "Due mani si tengono con affetto",
    },
    passeggiate: {
        n: "04",
        title: "Passeggiate al tuo ritmo",
        desc: "Una passeggiata quotidiana all'aria aperta: buona per il corpo, migliore per l'umore. Al tuo passo, senza fretta.",
        chips: ["All'aria aperta", "Ogni giorno", "Al tuo passo"],
    },
};

const NumberLabel = ({ n }) => (
    <span className="font-serif text-sm italic text-terracotta">({n})</span>
);

const Chips = ({ chips }) => (
    <ul className="flex flex-wrap gap-2">
        {chips.map((c) => (
            <li
                key={c}
                className="rounded-full border border-ink/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-mutedwarm"
            >
                {c}
            </li>
        ))}
    </ul>
);

const ImageCard = ({ img, alt, span, testid }) => (
    <Reveal
        className={`group relative overflow-hidden rounded-[28px] ${span}`}
        y={40}
    >
        <div data-testid={testid} className="relative h-full min-h-[340px]">
            <img
                src={img}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-ink/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            />
        </div>
    </Reveal>
);

const TextCard = ({ s, testid, className = "" }) => (
    <Reveal className={className} y={40}>
        <div
            data-testid={testid}
            className="group flex h-full min-h-[300px] flex-col justify-between gap-8 rounded-[28px] bg-stonewarm border border-line p-8 lg:p-10 transition-colors duration-500 hover:bg-sand"
        >
            <div className="flex items-start justify-between gap-4">
                <NumberLabel n={s.n} />
                <ArrowUpRight className="h-6 w-6 text-terracotta transition-transform duration-300 group-hover:rotate-45" />
            </div>
            <div className="space-y-4">
                <h3 className="font-serif text-2xl sm:text-3xl font-light tracking-tight text-ink">
                    {s.title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-mutedwarm">
                    {s.desc}
                </p>
                <Chips chips={s.chips} />
            </div>
        </div>
    </Reveal>
);

export const Services = () => (
    <section id="servizi" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="mb-14 lg:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                <Reveal className="lg:col-span-7">
                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-terracotta">
                        Cosa facciamo
                    </p>
                    <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
                        Piccoli gesti,
                        <br />
                        <em className="italic text-terracotta">grande compagnia.</em>
                    </h2>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-5">
                    <p className="text-base sm:text-lg leading-relaxed text-mutedwarm lg:pb-2">
                        Quattro modi di esserci, ogni giorno. Ogni servizio è
                        costruito intorno alla persona: tu decidi cosa serve,
                        noi portiamo tempo, pazienza e buona compagnia.
                    </p>
                </Reveal>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <TextCard
                    s={SERVICES.spese}
                    testid="service-card-spese"
                    className="lg:col-span-5"
                />
                <ImageCard
                    img={SERVICES.visite.img}
                    alt={SERVICES.visite.alt}
                    testid="service-card-visite"
                    span="lg:col-span-7 lg:row-span-2"
                />
                <TextCard
                    s={SERVICES.passeggiate}
                    testid="service-card-passeggiate"
                    className="lg:col-span-5"
                />
                <ImageCard
                    img={SERVICES.accompagnamento.img}
                    alt={SERVICES.accompagnamento.alt}
                    testid="service-card-accompagnamento"
                    span="lg:col-span-12"
                />
            </div>
        </div>
    </section>
);

export default Services;
